import { EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';

import {
  CasualTournamentEntity,
  EsRangeValues,
  EventsCountByTag,
  Game,
  GameTypeEnum,
  RoomFilterModel,
  TournamentsFilters,
  TournamentsResponse,
} from '@fantaz/shared/data-access/models';
import * as GameDetailsActions from './game-details.actions';

export const GAMEDETAILS_FEATURE_KEY = 'gameDetails';

export interface GameDetailsState {
  selectedId?: string | number;
  cashTournaments?: TournamentsResponse;
  prizeTournaments?: TournamentsResponse;
  game: Game | null;
  eventsCountByTag?: EventsCountByTag[];
  esRangeValues?: EsRangeValues;
  searchTerm?: string;
  filterByTags?: string[];
  filterByRooms?: RoomFilterModel[];
  existingGameIds?: Game[];
  filters?: TournamentsFilters;
  loaded: boolean;
  loadingMore: boolean;
  filtering: boolean;
  error?: string | null;
  errorMore?: string | null;
  pagesLoaded: { cash: number[]; prize: number[] };
}

export interface GameDetailsPartialState {
  readonly [GAMEDETAILS_FEATURE_KEY]: GameDetailsState;
}

export const gameDetailsAdapter: EntityAdapter<CasualTournamentEntity> =
  createEntityAdapter<CasualTournamentEntity>({
    selectId: (tournaments: CasualTournamentEntity) => tournaments.uuid,
  });

export const initialGameDetailsState: GameDetailsState =
  gameDetailsAdapter.getInitialState({
    loadingMore: false,
    filterByRooms: [],
    filters: {
      tournamentTags: [],
      gameTypes: [GameTypeEnum.ALL],
    },
    eventsCountByTag: [],
    game: null,
    loaded: false,
    filtering: false,
    searchTerm: '',
    pagesLoaded: { cash: [], prize: [] },
  });

const reducer = createReducer(
  initialGameDetailsState,
  on(GameDetailsActions.initGameDetails, (state, { params }) => ({
    ...state,
    filterByTags: params.tournamentTags || state.filterByTags || [],
    searchTerm: params.search || '',
    loaded: false,
    error: null,
  })),
  on(
    GameDetailsActions.loadGameDetailsSuccess,
    (state, { cashTournaments, prizeTournaments }) => {
      const cashGamesIds = cashTournaments?.existingGameIds;
      const prizeGamesIds = prizeTournaments?.existingGameIds;
      const existingGameIds = cashGamesIds || prizeGamesIds;

      return {
        ...state,
        loaded: true,
        cashTournaments,
        prizeTournaments,
        eventsCountByTag:
          cashTournaments?.eventsCountByTag ||
          prizeTournaments?.eventsCountByTag ||
          [],
        esRangeValues:
          cashTournaments?.esRangeValues ||
          prizeTournaments?.esRangeValues ||
          {},
        filters: cashTournaments?.filters || prizeTournaments?.filters || {},
        existingGameIds,
        game:
          cashTournaments?.items[0]?.game ||
          prizeTournaments?.items[0]?.game ||
          existingGameIds[0] ||
          null,
      };
    },
  ),
  on(GameDetailsActions.loadGameDetailsFailure, (state, { error }) => ({
    ...state,
    error,
  })),
  on(GameDetailsActions.loadMoreGameDetails, (state) => ({
    ...state,
    loadingMore: true,
    errorMore: null,
  })),
  on(
    GameDetailsActions.loadMoreGameDetailsSuccess,
    (state, { cashTournaments, prizeTournaments }) => {
      const updatedCashTournamentsItems = [
        ...(state.cashTournaments?.items || []),
        ...(cashTournaments?.items || []),
      ];
      const updatedPrizeTournamentsItems = [
        ...(state.prizeTournaments?.items || []),
        ...(prizeTournaments?.items || []),
      ];
      return {
        ...state,
        cashTournaments: {
          ...cashTournaments,
          items: updatedCashTournamentsItems,
        },
        prizeTournaments: {
          ...prizeTournaments,
          items: updatedPrizeTournamentsItems,
        },
        loadingMore: false,
      };
    },
  ),
  on(GameDetailsActions.loadMoreGameDetailsFailure, (state, { error }) => ({
    ...state,
    loadingMore: false,
    errorMore: error,
  })),
  on(GameDetailsActions.filterGameDetails, (state, { params }) => ({
    ...state,
    filtering: true,
    filterByTags: params.tournamentTags || [],
    searchTerm: params.search,
    error: null,
  })),
  on(
    GameDetailsActions.filterGameDetailsSuccess,
    (state, { cashTournaments, prizeTournaments }) => {
      return {
        ...state,
        filtering: false,
        cashTournaments,
        prizeTournaments,
      };
    },
  ),
  on(GameDetailsActions.filterGameDetailsFailure, (state, { error }) => ({
    ...state,
    filtering: false,
    error: error,
  })),
  on(GameDetailsActions.clearFilterGameDetailsByTag, (state) => ({
    ...state,
    filterByTags: [],
  })),

  on(GameDetailsActions.clearSearchGameDetails, (state) => ({
    ...state,
    searchTerm: '',
  })),
  on(GameDetailsActions.clearRoomFilters, (state) => ({
    ...state,
    filterByRooms: [],
  })),
  on(GameDetailsActions.restoreGameDetails, (state, { payload }) => ({
    ...state,
    game: {
      ...state.game,
      ...payload,
    },
  })),
);

export function gameDetailsReducer(
  state: GameDetailsState | undefined,
  action: Action,
) {
  return reducer(state, action);
}
